import { z } from 'zod';
import {
  dataDoFatoSchema,
  dataHoraSchema,
  dataSchema,
  dinheiroSchema,
  paginacaoSchema,
  precoUnitarioSchema,
  quantidadeSchema,
  respostaListaSchema,
  situacaoAprovacaoSchema,
  uuidSchema,
} from './comum.ts';

/**
 * Lista de compra direta (`.spec/015`, decisão 46).
 *
 * É o **caminho curto da compra**: a obra monta uma lista do material de
 * reposição rotineira — cimento, areia, brita, EPI —, e o Compras a transforma em
 * pedido. Não há solicitação, não há cotação, e não há preço a perguntar: o
 * fornecedor e o valor já estão no cadastro do insumo, negociados uma vez
 * (`insumos.permite_compra_direta`).
 *
 * ## O que ela não é
 *
 * **Não é uma solicitação de compra.** A solicitação diz "preciso disto" e abre
 * um saldo que a cotação e o pedido consomem aos poucos, com atendimento parcial,
 * excedente e encerramento. A lista diz "compre isto, deste fornecedor, por este
 * preço": ela vira pedido de uma vez e acabou. Guardar as duas na mesma tabela
 * obrigaria metade das colunas de atendimento a ficar sempre em zero, e a tela de
 * solicitação a explicar por que aquelas linhas nunca são cotadas.
 *
 * **Não é um pedido.** O pedido é compromisso com o fornecedor, tem imposto,
 * frete, rateio e condição de pagamento. A lista é o pedaço de papel que a obra
 * manda para o Compras.
 *
 * ## Três decisões
 *
 * 1. **Obra e depósito são obrigatórios.** É o que distingue a lista de um pedido
 *    digitado à mão: ela nasce de um canteiro que precisa do material, e é dela
 *    que sai o rateio do pedido gerado. Sem obra, o custo não chega a lugar
 *    nenhum; sem depósito, o material não tem para onde entrar.
 * 2. **Só material com compra direta.** O seletor recorta, a API recusa e a
 *    geração confere de novo — é regra que atravessa as três camadas de propósito
 *    (`CLAUDE.md`), porque o material pode perder a marca entre montar a lista e
 *    gerar o pedido.
 * 3. **O preço não é gravado na lista.** Ele vem do cadastro do insumo a cada
 *    leitura, e por isso muda quando o acordo muda. Não é descuido: a lista é
 *    **previsão**, como o custo de estoque do pedido, e congelá-la faria a obra
 *    olhar um preço que o Compras não vai pagar. Quem congela é o pedido, na
 *    geração (ADR 0004).
 */

/* -------------------------------------------------------------------------- */
/* Situação                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * O ciclo da lista, e ele é curto.
 *
 * `GERADA` **não é escolha de ninguém**: ela é derivada dos pedidos que os itens
 * geraram, e o próprio pedido a escreve — criar, alterar ou excluir um pedido
 * recalcula a situação das listas que ele tocava. É a mesma mecânica da
 * solicitação e da cotação (`.spec/015`, decisão 46), e é o que faz o pedido
 * excluído devolver a lista ao caminho sem que exista um botão de "reabrir".
 *
 * `ENCERRADA` é a saída manual: a obra desistiu, ou comprou por fora. Ela existe
 * porque a lista sem ela só sairia da tela virando pedido — e o pedido que
 * ninguém quer fazer é justamente o que não se deve obrigar a fazer.
 */
export const SITUACOES_LISTA_COMPRA_DIRETA = ['PENDENTE', 'GERADA', 'ENCERRADA'] as const;
export const situacaoListaCompraDiretaSchema = z.enum(SITUACOES_LISTA_COMPRA_DIRETA);
export type SituacaoListaCompraDireta = z.infer<typeof situacaoListaCompraDiretaSchema>;

export const ROTULO_SITUACAO_LISTA_COMPRA_DIRETA: Readonly<
  Record<SituacaoListaCompraDireta, string>
> = {
  PENDENTE: 'Pendente',
  GERADA: 'Gerada',
  ENCERRADA: 'Encerrada',
};

/**
 * A lista ainda se altera?
 *
 * Vive no contrato porque a tela e a API precisam da **mesma** regra: o
 * formulário decide se mostra os botões de gravar, e o service decide se recusa a
 * requisição. Duas cópias divergiriam, e a divergência apareceria como botão que
 * o servidor recusa — o pior dos dois defeitos, porque o usuário já clicou.
 *
 * A gerada não se altera porque há pedido apontando para os itens dela: mudar a
 * quantidade aqui não muda o que já foi comprado, e deixaria os dois documentos
 * contando histórias diferentes. Quem errou desfaz o pedido, e a lista volta
 * sozinha a `PENDENTE`.
 */
export function listaCompraDiretaEditavel(situacao: SituacaoListaCompraDireta): boolean {
  return situacao === 'PENDENTE';
}

/**
 * A lista entra na geração de pedidos?
 *
 * Só a pendente. A encerrada saiu de circulação por decisão de quem a montou, e
 * a gerada já virou compromisso — oferecê-la de novo na tela de geração é como se
 * duplica um pedido sem perceber.
 */
export function listaVaiParaPedido(situacao: SituacaoListaCompraDireta): boolean {
  return situacao === 'PENDENTE';
}

/* -------------------------------------------------------------------------- */
/* Itens                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Teto de itens por lista.
 *
 * Sanidade, não regra de negócio: uma lista de canteiro tem dez ou vinte linhas,
 * e quem passar de duzentas quase certamente colou uma planilha inteira. O limite
 * transforma isso em erro em vez de num documento que ninguém consegue conferir —
 * e num `INSERT` de duzentas linhas que o driver monta parâmetro a parâmetro.
 */
export const MAXIMO_ITENS_LISTA_COMPRA_DIRETA = 200;

/** Quantidade pedida: **sempre positiva**. Lista com zero linhas de nada. */
const quantidadeDaListaSchema = quantidadeSchema.refine(
  (v) => Number(v) > 0,
  'A quantidade tem de ser maior que zero.',
);

/**
 * Um item da lista, como o cliente o manda.
 *
 * **Quatro ausências, e as quatro são a mesma decisão: o servidor é quem sabe.**
 *
 * - `fornecedorId` e `valorUnitario` vêm do cadastro do insumo. Aceitá-los aqui
 *   deixaria a obra pedir a um fornecedor que ninguém negociou, pelo preço que
 *   ela quisesse — que é exatamente o que a compra direta existe para não fazer.
 * - `depositoId` é do cabeçalho: a lista inteira é de um canteiro. Um depósito
 *   por linha faria a lista servir a dois locais, e aí ela é uma solicitação.
 * - `pedidoCompraItemId` é escrito pela geração, e só por ela. Aceitá-lo deixaria
 *   uma lista se declarar gerada sem pedido nenhum atrás.
 */
export const itemListaCompraDiretaEntradaSchema = z.object({
  /** Precisa ter `permiteCompraDireta`. A API confere; o seletor já recorta. */
  insumoId: uuidSchema,
  quantidade: quantidadeDaListaSchema,
  /**
   * Data prevista de entrega, **por item**.
   *
   * No item e não no cabeçalho porque é a única coisa que costuma diferir entre
   * as linhas da mesma lista: o cimento é para segunda, a esquadria é para o mês
   * que vem. Um prazo único obrigaria a abrir duas listas para o mesmo canteiro.
   *
   * Opcional: a lista de reposição rotineira não tem data — é "quando der". Um
   * campo obrigatório aqui faria a obra inventar uma data, e data inventada num
   * pedido é cobrança que o comprador vai fazer sem motivo.
   */
  dataEntrega: dataSchema.nullable().prefault(null),
  observacao: z
    .string()
    .trim()
    .max(500, 'A observação do item tem no máximo 500 caracteres.')
    .prefault(''),
});

export type ItemListaCompraDiretaEntrada = z.infer<typeof itemListaCompraDiretaEntradaSchema>;

/**
 * Um item como a API o devolve.
 *
 * O fornecedor e o preço vêm **do cadastro do insumo**, resolvidos na leitura —
 * não estão gravados aqui. Ver a decisão 3 no topo do arquivo: a lista é
 * previsão, e o número que ela mostra é o acordo de hoje.
 *
 * `valorTotal` é `quantidade × preço`, calculado pelo servidor pela mesma função
 * que a tela usa (`totalDoItemDaLista`). Gravá-lo permitiria um total que não
 * confere com os fatores.
 */
export const itemListaCompraDiretaSchema = z.object({
  id: uuidSchema,
  insumoId: uuidSchema,
  insumoNome: z.string().nullable(),
  /** Sigla da unidade — a listagem escreve "12 SC", não "12". */
  insumoUnidade: z.string().nullable(),
  /** Decide se a quantidade se exibe com decimais. */
  insumoApenasInteiros: z.boolean(),
  /** Casas do preço deste insumo, para o campo e a exibição usarem a escala dele. */
  casasDecimaisPreco: z.number().int(),
  quantidade: quantidadeSchema,
  dataEntrega: dataSchema.nullable(),
  observacao: z.string(),

  /**
   * O acordo, **como está hoje no cadastro do insumo**.
   *
   * Nulos quando o material perdeu a marca de compra direta depois de a lista ter
   * sido montada. A tela mostra a linha assim mesmo, marcada — esconder o item
   * faria a lista parecer menor do que é —, e a geração recusa nomeando a linha.
   */
  fornecedorId: uuidSchema.nullable(),
  fornecedorNome: z.string().nullable(),
  valorUnitario: precoUnitarioSchema.nullable(),
  valorTotal: dinheiroSchema.nullable(),
  /** Falso quando o material deixou de permitir compra direta. Ver acima. */
  permiteCompraDireta: z.boolean(),

  /** O item de pedido que esta linha gerou. Nulo enquanto a lista é pendente. */
  pedidoCompraItemId: uuidSchema.nullable(),
  pedidoCompraId: uuidSchema.nullable(),
  pedidoCompraNumero: z.number().int().nullable(),
});

export type ItemListaCompraDireta = z.infer<typeof itemListaCompraDiretaSchema>;

/**
 * `quantidade × preço`, a centavo.
 *
 * No contrato, e não no service: a tela mostra o total enquanto o usuário digita,
 * e o servidor devolve o mesmo número. Duas implementações discordam num
 * arredondamento, e o total muda ao salvar.
 *
 * `BigInt` sobre inteiros escalados, e não `Number`: `decimal.js` é dependência
 * de `@construtora/domain`, que o front não carrega, e ponto flutuante perde
 * centavo em silêncio (ADR 0003). Quantidade tem 4 casas, preço tem 6, e o
 * produto sai com 10 — o arredondamento para 2 é meia-unidade para cima, como em
 * todo dinheiro do sistema.
 */
export function totalDoItemDaLista(
  quantidade: string,
  valorUnitario: string | null,
): string | null {
  if (valorUnitario === null) return null;

  const escalar = (valor: string, casas: number): bigint => {
    const [inteira = '0', decimais = ''] = valor.replace('-', '').split('.');
    const preenchidas = (decimais + '0'.repeat(casas)).slice(0, casas);
    const sinal = valor.startsWith('-') ? -1n : 1n;

    return sinal * BigInt(inteira + preenchidas);
  };

  // 4 casas × 6 casas = 10 casas. Divide-se por 10^8 para chegar aos centavos,
  // com meia-unidade para cima — o mesmo arredondamento do resto do sistema.
  const produto = escalar(quantidade, 4) * escalar(valorUnitario, 6);
  const divisor = 10n ** 8n;
  const centavos = (produto + divisor / 2n) / divisor;

  return `${centavos / 100n}.${String(centavos % 100n).padStart(2, '0')}`;
}

/* -------------------------------------------------------------------------- */
/* Documento                                                                  */
/* -------------------------------------------------------------------------- */

const itensSchema = z
  .array(itemListaCompraDiretaEntradaSchema)
  .min(1, 'A lista precisa de pelo menos um material.')
  .max(
    MAXIMO_ITENS_LISTA_COMPRA_DIRETA,
    `A lista tem no máximo ${MAXIMO_ITENS_LISTA_COMPRA_DIRETA} materiais.`,
  );

/**
 * Payload de gravação.
 *
 * **Três ausências.** `numero` é do contador da conta, na transação que cria a
 * lista — aceitá-lo do cliente deixaria dois documentos nascerem com o mesmo, e o
 * segundo descobriria isso como erro de banco. `situacao` é derivada dos pedidos
 * (ver `SITUACOES_LISTA_COMPRA_DIRETA`), e um `PATCH {situacao}` convidaria o
 * cliente a tentar `GERADA` sem pedido nenhum. E o **fornecedor** não aparece em
 * lugar nenhum: quem o diz é o cadastro de cada material.
 */
export const criarListaCompraDiretaSchema = z
  .object({
    /** Empresa do grupo — `pessoas` com `is_empresa`. É a dona do depósito. */
    empresaId: uuidSchema,
    /** Dia em que a lista foi montada. Data de negócio, sem hora. */
    dataLancamento: dataDoFatoSchema,
    /** **Obrigatória**: é dela que sai o rateio do pedido gerado. */
    obraId: uuidSchema,
    /** **Obrigatório**: é para onde o material entra. Da empresa da lista. */
    depositoId: uuidSchema,
    observacao: z
      .string()
      .trim()
      .max(1000, 'A observação tem no máximo 1000 caracteres.')
      .prefault(''),
    itens: itensSchema,
  })
  .superRefine((dados, ctx) => {
    /*
      O mesmo material duas vezes na lista.

      O índice único recusaria de qualquer forma, com uma violação de constraint
      que o usuário não consegue interpretar. Aqui o erro aponta a **linha**, que
      é o que permite à tela destacá-la — e a correção é somar as duas
      quantidades numa só, não escolher qual apagar.
    */
    const vistos = new Set<string>();

    dados.itens.forEach((item, indice) => {
      if (vistos.has(item.insumoId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['itens', indice, 'insumoId'],
          message: 'Este material já está em outra linha. Some as quantidades numa linha só.',
        });
      }
      vistos.add(item.insumoId);
    });
  });

export type CriarListaCompraDireta = z.infer<typeof criarListaCompraDiretaSchema>;

export const atualizarListaCompraDiretaSchema = criarListaCompraDiretaSchema;
export type AtualizarListaCompraDireta = z.infer<typeof atualizarListaCompraDiretaSchema>;

/**
 * A lista como a API a devolve.
 *
 * Os nomes de empresa, obra e depósito vêm **desnormalizados**: a listagem mostra
 * "Residencial Aurora · Canteiro Aurora" e não três uuid, e resolver isso no
 * cliente significaria três consultas por linha.
 */
export const listaCompraDiretaSchema = z.object({
  id: uuidSchema,
  /**
   * O número sequencial do documento, **do servidor**.
   *
   * Único na conta inteira, independentemente da empresa do grupo: é o número que
   * a obra diz ao telefone, e ele não deve exigir dizer também de qual empresa se
   * fala. Mesma regra da solicitação e do pedido.
   */
  numero: z.number().int(),
  empresaId: uuidSchema,
  empresaNome: z.string().nullable(),
  dataLancamento: dataSchema,
  obraId: uuidSchema,
  obraNome: z.string().nullable(),
  depositoId: uuidSchema,
  depositoNome: z.string().nullable(),
  situacao: situacaoListaCompraDiretaSchema,
  /**
   * Por que a lista foi encerrada à mão. Nulo nas demais situações.
   *
   * Vem na leitura, e não fica só no banco: "encerrada" sozinho não responde a
   * pergunta que se faz seis meses depois — por que aquilo que a obra pediu não
   * virou pedido. É o motivo que a tela mostra no aviso do documento em consulta.
   */
  motivoEncerramento: z.string().nullable(),
  /** Da autorização (`aprovacao-compra.ts`, decisão 108). */
  situacaoAprovacao: situacaoAprovacaoSchema,
  observacao: z.string(),
  /**
   * Soma dos itens, pelo acordo de hoje. Nula quando **algum** item perdeu o
   * acordo — um total que ignora a linha sem preço mente por omissão.
   */
  valorTotal: dinheiroSchema.nullable(),
  itens: z.array(itemListaCompraDiretaSchema),
  createdAt: dataHoraSchema,
  updatedAt: dataHoraSchema,
});

export type ListaCompraDireta = z.infer<typeof listaCompraDiretaSchema>;

export const listaListasCompraDiretaSchema = respostaListaSchema(listaCompraDiretaSchema);
export type ListaListasCompraDireta = z.infer<typeof listaListasCompraDiretaSchema>;

/**
 * Filtros da listagem.
 *
 * Sem `ativo`: lista é documento de movimento, não cadastro — ela existe ou não
 * existe, e o que a tira de circulação é a situação. O recorte natural é **obra e
 * situação** ("o que a Aurora ainda tem para comprar"), que é a pergunta que a
 * tela abre respondendo.
 */
export const listarListasCompraDiretaQuerySchema = paginacaoSchema.extend({
  empresaId: uuidSchema.optional(),
  obraId: uuidSchema.optional(),
  depositoId: uuidSchema.optional(),
  situacao: situacaoListaCompraDiretaSchema.optional(),
  /**
   * A fila do autorizador: `AGUARDANDO` traz o que espera decisão (decisão
   * 108). Igualdade sobre a coluna, como a situação.
   */
  situacaoAprovacao: situacaoAprovacaoSchema.optional(),
  /** Início do período, inclusivo. Filtra `dataLancamento`. */
  de: dataSchema.optional(),
  /** Fim do período, inclusivo. */
  ate: dataSchema.optional(),
});

export type ListarListasCompraDiretaQuery = z.infer<typeof listarListasCompraDiretaQuerySchema>;

export const COLUNAS_ORDENAVEIS_LISTA_COMPRA_DIRETA = [
  'numero',
  'dataLancamento',
  'createdAt',
] as const;

/* -------------------------------------------------------------------------- */
/* Encerramento                                                               */
/* -------------------------------------------------------------------------- */

/**
 * O encerramento manual, com motivo obrigatório.
 *
 * Motivo obrigatório porque encerrar é tirar da tela algo que a obra pediu: seis
 * meses depois, "por que esta lista não virou pedido" é a pergunta, e "encerrada"
 * sozinho não a responde. É a mesma regra da solicitação.
 */
export const encerrarListaCompraDiretaSchema = z.object({
  motivo: z
    .string()
    .trim()
    .min(3, 'Escreva por que a lista está sendo encerrada.')
    .max(500, 'O motivo tem no máximo 500 caracteres.'),
});

export type EncerrarListaCompraDireta = z.infer<typeof encerrarListaCompraDiretaSchema>;

/* -------------------------------------------------------------------------- */
/* Geração de pedidos                                                         */
/* -------------------------------------------------------------------------- */

/**
 * O pedido de geração: as listas escolhidas na tela.
 *
 * **Listas inteiras, e não itens soltos.** A alternativa era deixar marcar linha
 * a linha, como a tela de propostas da cotação faz. Foi descartada porque as duas
 * telas respondem perguntas diferentes: lá o comprador **decide** de quem comprar,
 * proposta a proposta; aqui a decisão já está no cadastro, e o que sobra é "estas
 * listas viram pedido agora". Marcar meia lista deixaria a outra metade pendente
 * sem que nada dissesse por quê.
 */
export const gerarPedidosDeListasSchema = z.object({
  listaIds: z
    .array(uuidSchema)
    .min(1, 'Escolha ao menos uma lista.')
    .max(50, 'São no máximo 50 listas por geração.')
    .refine((ids) => new Set(ids).size === ids.length, 'Há lista repetida na seleção.'),
  /**
   * A data dos pedidos gerados. Ausente, hoje.
   *
   * Hoje e não a data da lista: a lista é de terça, e o compromisso com o
   * fornecedor está sendo assumido agora. É a mesma escolha da geração por
   * cotação.
   */
  dataLancamento: dataDoFatoSchema.optional(),
});

export type GerarPedidosDeListas = z.infer<typeof gerarPedidosDeListasSchema>;

/**
 * O que a geração fez.
 *
 * `pedidoIds` conta **pedidos**, e não listas: cinco listas do mesmo fornecedor
 * viram um pedido só, e três materiais de fornecedores diferentes na mesma lista
 * viram três. O agrupamento é por empresa × fornecedor × condição de pagamento —
 * os três são campos do cabeçalho do pedido, e duas linhas que discordem em
 * qualquer um deles não cabem no mesmo documento.
 */
export const pedidosGeradosDeListasSchema = z.object({
  pedidoIds: z.array(uuidSchema),
  /** Quantas listas entraram. Nem sempre é `listaIds.length` — ver a rota. */
  listasGeradas: z.number().int(),
});

export type PedidosGeradosDeListas = z.infer<typeof pedidosGeradosDeListasSchema>;
